import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { SuggestionPickerModal } from './SuggestionPickerModal';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Button,
  Input,
  SearchBox,
  Spinner,
  Text,
  tokens,
  Card,
  CardHeader,
  Subtitle2,
  MessageBar,
  MessageBarBody,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarGroup,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
} from '@fluentui/react-components';
import {
  Add20Regular,
  Copy20Regular,
  Dismiss20Regular,
  Eye20Regular,
  List20Regular,
  ArrowClockwise20Regular,
  OpenRegular,
  Search20Regular,
} from '@fluentui/react-icons';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';
import { CaseInsensitiveSearchParams } from '../utils/CaseInsensitiveSearchParams.mjs';
import TimezoneDateTime from './TimezoneDateTime';
import { POLARIS_CARD_CAPTION_PADDING_Y } from './polarisSharedStyles';

const FETCH_SIZE = 50;
const FETCH_SIZE_SMALL = 25;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding('0'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    overflow: 'hidden',
    contain: 'layout style paint',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    ...shorthands.padding(tokens.spacingHorizontalM),
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  searchInput: {
    width: '140px',
  },
  keywordSearchInput: {
    width: '165px',
  },
  keywordSearchWithHelp: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    ...shorthands.gap(tokens.spacingHorizontalXXS),
  },
  keywordPopoverSurface: {
    position: 'absolute',
    top: `calc(100% + ${tokens.spacingVerticalXS})`,
    right: 0,
    zIndex: 1000,
    width: '360px',
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  keywordHelpList: {
    marginTop: '0',
    marginBottom: '0',
    paddingLeft: tokens.spacingHorizontalXL,
  },
  keywordExample: {
    display: 'block',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    overflowWrap: 'anywhere',
  },
  toolbar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow2,
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  toolbarFarGroup: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  section: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionEmpty: {
    flex: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    ...shorthands.gap(tokens.spacingHorizontalM),
    width: '100%',
    ...shorthands.padding(POLARIS_CARD_CAPTION_PADDING_Y, tokens.spacingHorizontalM),
  },
  cardHeaderSpacer: {
    flex: 1,
  },
  countInfo: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 0,
  },
  loadingMore: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalS),
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  cellContent: {
    display: 'block',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
  },
  headerRow: {
    backgroundColor: tokens.colorBrandBackground,
    '& .fui-TableHeaderCell__button, & .fui-TableHeaderCell__button:hover, & .fui-TableHeaderCell__button:active, & .fui-TableHeaderCell__button:focus-visible': {
      backgroundColor: 'transparent',
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    '& .fui-TableHeaderCell__button *': {
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    '& [role="button"], & [role="button"]:hover, & [role="button"]:active, & [role="button"]:focus-visible': {
      backgroundColor: 'transparent',
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    '& [role="button"] *': {
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
  },
  headerCell: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    // fontSize: tokens.fontSizeBase200,
    // minWidth: 0,
    // overflow: 'hidden',
    ...shorthands.padding(0, tokens.spacingHorizontalSNudge),
    cursor: 'inherit',
  },
  headerCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%',
    minWidth: 0,
  },
  sortableHeaderCell: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  dataCell: {
    fontSize: tokens.fontSizeBase200,
    minWidth: 0,
    overflow: 'hidden',
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalSNudge),
  },
  dataRow: {
    fontSize: tokens.fontSizeBase200,
    ':nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      cursor: 'pointer',
    },
  },
  scrollBody: {
    flex: 1,
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
  },
  emptyState: {
    ...shorthands.padding('0', tokens.spacingHorizontalM, tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  emptyMessageBar: {
    ...shorthands.margin('0'),
  },
  dataGridCompact: {
    fontSize: tokens.fontSizeBase200,
  },
  stickyGridHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    ...shorthands.padding('0'),
    ...shorthands.gap('0'),
    rowGap: '0',
    columnGap: '0',
    '& .ms-Card-content': {
      ...shorthands.padding('0'),
    },
    '& > div': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="CardHeader"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="ms-Card-header"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
  },
  cardEmpty: {
    display: 'flex',
    flexDirection: 'column',
    flex: 0,
    height: 'auto',
    overflow: 'visible',
    ...shorthands.padding('0'),
    ...shorthands.gap('0'),
    rowGap: '0',
    columnGap: '0',
    '& .ms-Card-content': {
      ...shorthands.padding('0'),
    },
    '& > div': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="CardHeader"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
    '& [class*="ms-Card-header"]': {
      ...shorthands.padding('0'),
      ...shorthands.margin('0'),
    },
  },
  cardBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.padding('0'),
  },
  cardBodyEmpty: {
    flex: 0,
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
});

function JobTable({ title, jobs, totalCount, loading, loadingMore, error, onLoadMore, hasMore, showError, showSignoff, onViewJob, onCloneJob, onCancelJob, allJobsEmpty, sortColumn, sortOrder, onSort }) {
  const classes = useStyles();
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [flexColumnWidth, setFlexColumnWidth] = useState(400);

  const flexColumnCount = useMemo(() => {
    let count = 2; // Name + Note
    if (showError) count++;
    if (showSignoff) count++;
    return count;
  }, [showError, showSignoff]);

  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth === 0) return;
      // Fixed columns: User(90) + Type(160) + Priority(40) + DateStart(140) + Actions(70)
      const fixedTotal = 90 + 160 + 40 + 140 + 70;
      const available = containerWidth - fixedTotal;
      setFlexColumnWidth(Math.max(150, Math.floor(available / flexColumnCount)));
    };
    calculateWidth();
    const observer = new ResizeObserver(calculateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [flexColumnCount]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      onLoadMore();
    }
  }, [loadingMore, hasMore, onLoadMore]);

  const sortableColumns = ['User', 'Type', 'Priority', 'DateStart', 'Name', 'Note'];
  const [hoveredColumn, setHoveredColumn] = useState(null);

  const renderHeaderWithSort = (label) => label;

  const columns = [
    createTableColumn({
      columnId: 'User',
      renderHeaderCell: () => renderHeaderWithSort('User'),
       renderCell: (item) => (
         <span className={classes.cellContent} title={item.User || ''}>{item.User}</span>
       ),
    }),
    createTableColumn({
      columnId: 'Type',
      renderHeaderCell: () => renderHeaderWithSort('TestType'),
       renderCell: (item) => (
         <span className={classes.cellContent} title={item.Type || ''}>{item.Type}</span>
       ),
    }),
    createTableColumn({
      columnId: 'Priority',
      renderHeaderCell: () => renderHeaderWithSort('P'),
      renderCell: (item) => item.Priority,
    }),
    createTableColumn({
      columnId: 'DateStart',
      renderHeaderCell: () => <>DateStart <sup style={{ fontSize: '0.72em', fontWeight: 400, opacity: 0.65 }}>PT</sup></>,
      renderCell: (item) => <TimezoneDateTime value={item.DateStart} className={classes.cellContent} />,
    }),
    createTableColumn({
      columnId: 'Name',
      renderHeaderCell: () => renderHeaderWithSort('Name'),
       renderCell: (item) => (
         <span className={classes.cellContent} title={item.Name || ''}>{item.Name}</span>
       ),
    }),
    createTableColumn({
      columnId: 'Note',
      renderHeaderCell: () => renderHeaderWithSort('Note'),
       renderCell: (item) => (
         <span className={classes.cellContent} title={item.Note || ''}>{item.Note}</span>
       ),
    }),
  ];

  if (showError) {
    columns.push(
      createTableColumn({
        columnId: 'Error',
        renderHeaderCell: () => 'Error',
         renderCell: (item) => (
           <span className={classes.cellContent} title={item.Error || ''}>{item.Error}</span>
         ),
      })
    );
  }

  if (showSignoff) {
    columns.push(
      createTableColumn({
        columnId: 'SignoffResult',
        renderHeaderCell: () => 'SignoffResult',
         renderCell: (item) => (
           <span className={classes.cellContent} title={item.SignoffResult || ''}>{item.SignoffResult}</span>
         ),
      })
    );
  }

  columns.push(
    createTableColumn({
      columnId: 'Actions',
      renderHeaderCell: () => 'Actions',
      renderCell: (item) => (
        <>
          <Button
            appearance="subtle"
            icon={<OpenRegular />}
            title="Open in new window"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/polaris/job/${item.Id}`, '_blank');
            }}
          />
          <Button
            appearance="subtle"
            icon={<Copy20Regular />}
            title="Clone job"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCloneJob(item);
            }}
          />
          {item.Status && !['finished', 'succeeded', 'failed', 'cancelled'].includes(item.Status.toLowerCase()) && (
            <Button
              appearance="subtle"
              icon={<Dismiss20Regular />}
              title="Cancel job"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCancelJob(item.Id, item.Tests);
              }}
            />
          )}
        </>
      ),
    })
  );

  const columnSizingOptions = {
    User: { minWidth: 80, defaultWidth: 90 },
    Type: { minWidth: 100, defaultWidth: 160 },
    Priority: { minWidth: 30, defaultWidth: 40 },
    DateStart: { minWidth: 120, defaultWidth: 140 },
    Name: { minWidth: 150, defaultWidth: flexColumnWidth },
    Note: { minWidth: 100, defaultWidth: flexColumnWidth },
    Error: { minWidth: 100, defaultWidth: flexColumnWidth },
    SignoffResult: { minWidth: 80, defaultWidth: flexColumnWidth },
    Actions: { minWidth: 60, defaultWidth: 70 },
  };

  return (
    <div ref={containerRef} className={jobs.length === 0 && !loading && !allJobsEmpty ? classes.sectionEmpty : classes.section}>
      <Card className={jobs.length === 0 && !loading && !allJobsEmpty ? classes.cardEmpty : classes.card}>
        <CardHeader
          header={
            <div className={classes.cardHeader}>
              <Subtitle2>{title}</Subtitle2>
              {jobs.length > 0 && (
                <>
                  <div className={classes.cardHeaderSpacer} />
                  <Text size={200} className={classes.countInfo}>
                    {jobs.length} of {totalCount.toLocaleString()} entries
                  </Text>
                </>
              )}
            </div>
          }
        />
        <div className={jobs.length === 0 && !loading && !allJobsEmpty ? classes.cardBodyEmpty : classes.cardBody}>
          {loading ? (
            <div className={classes.loading}>
              <Spinner size="small" label="Loading..." />
            </div>
          ) : error ? (
            <div className={classes.emptyState}>
              <MessageBar intent="error" className={classes.emptyMessageBar}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            </div>
          ) : jobs.length === 0 ? (
            <div className={classes.emptyState}>
              <MessageBar intent="info" className={classes.emptyMessageBar}>
                <MessageBarBody>No jobs found</MessageBarBody>
              </MessageBar>
            </div>
          ) : (
            <div className={classes.scrollBody} ref={scrollRef} onScroll={handleScroll}>
              <DataGrid
                key={flexColumnWidth}
                items={jobs}
                columns={columns}
                getRowId={(item) => item.Id}
                resizableColumns
                columnSizingOptions={columnSizingOptions}
                size="extra-small"
                className={classes.dataGridCompact}
              >
                <DataGridHeader className={classes.stickyGridHeader}>
                  <DataGridRow className={classes.headerRow}>
                    {({ renderHeaderCell, columnId }) => {
                      const isSortable = sortableColumns.includes(columnId);
                      const isActive = sortColumn === columnId;

                      const handleClick = () => {
                        if (!isSortable) return;
                        if (sortColumn === columnId) {
                          onSort(sortColumn, sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          onSort(columnId, 'asc');
                        }
                      };

                      return (
                        <DataGridHeaderCell
                          className={mergeClasses(
                            classes.headerCell,
                            isSortable ? classes.sortableHeaderCell : undefined
                          )}
                          onClick={isSortable ? handleClick : undefined}
                        >
                          <div className={classes.headerCellContent}>
                            {renderHeaderCell()}
                            {isActive && (
                              <span style={{ fontSize: '12px', fontWeight: '600', flexShrink: 0 }}>
                                {sortOrder === 'asc' ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </DataGridHeaderCell>
                      );
                    }}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                  {({ item, rowId }) => (
                    <DataGridRow key={rowId} className={classes.dataRow} onClick={() => onViewJob(item.Id)}>
                      {({ renderCell }) => (
                        <DataGridCell className={classes.dataCell}>
                          {renderCell(item)}
                        </DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
              {loadingMore && (
                <div className={classes.loadingMore}>
                  <Spinner size="tiny" label="Loading more..." />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function PolarisPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getAccessToken, accounts } = useAuth();

  const handleViewJob = (jobId) => navigate(`/polaris/job/${jobId}`);
  const handleCloneJob = async (job) => {
    try {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs/${job.Id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Failed to fetch job details (status ${resp.status})`);
      const fullJob = await resp.json();
      navigate('/polaris/submit', { state: { cloneJob: fullJob } });
    } catch (err) {
      alert(err.message || 'Failed to fetch job details for cloning');
    }
  };
  const handleCancelJob = async (jobId, tests) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) return;
    try {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ Tests: tests || [] }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        alert(errData.message || `Failed to cancel job (status ${resp.status})`);
        return;
      }
      const data = await resp.json().catch(() => ({}));
      if (data.message && data.message !== 'Job cancelled successfully') {
        alert(data.message);
      }
      queryClient.invalidateQueries({ queryKey: ['polaris-jobs'] });
    } catch (err) {
      alert(err.message || 'Failed to cancel job');
    }
  };

  const urlParams = new CaseInsensitiveSearchParams(location.search);
  const currentUserAlias = accounts[0]?.username?.split('@')[0] || '';
  const queryUser = urlParams.get('user') || '';
  const queryTestType = urlParams.get('testType') || '';
  const queryDate = urlParams.get('date') || '';
  const queryKeyword = urlParams.get('keyword') || '';
  const [searchAlias, setSearchAlias] = useState(queryUser);
  const [searchTestType, setSearchTestType] = useState(queryTestType);
  const [searchDate, setSearchDate] = useState(queryDate);
  const [isKeywordHelpOpen, setIsKeywordHelpOpen] = useState(false);
  const keywordDraftRef = useRef(queryKeyword);
  const keywordInputRef = useRef(queryKeyword);
  const [committedKeyword, setCommittedKeyword] = useState(queryKeyword);

  // Sorting state for each task table
  const [runningSort, setRunningSort] = useState({ column: 'DateStart', order: 'desc' });
  const [queuedSort, setQueuedSort] = useState({ column: 'DateStart', order: 'desc' });
  const [completedSort, setCompletedSort] = useState({ column: 'DateStart', order: 'desc' });

  const RUNNING_FILTER = "contains(Status, 'OnGoing') or contains(Status, 'Start')";
  const QUEUED_FILTER = "contains(Status, 'Ready') or contains(Status, 'Submitted')";
  const COMPLETED_FILTER = "contains(Status, 'Finished')";

  const extraFilters = useMemo(() => {
    const esc = (s) => s.replace(/'/g, "''");
    const parts = [];
    if (searchAlias) parts.push(`contains(User, '${esc(searchAlias)}')`);
    if (searchTestType) parts.push(`contains(Type, '${esc(searchTestType)}')`);
    if (committedKeyword) {
      const prefixFields = {
        name: 'Name',
        note: 'Note',
        mt: 'MachineType',
        err: 'Error',
      };
      const keywordTokens = committedKeyword.trim().split(/\s+/).filter(Boolean);

      keywordTokens.forEach((token) => {
        const separatorIndex = token.indexOf(':');
        const prefix = separatorIndex > 0 ? token.slice(0, separatorIndex).toLowerCase() : '';
        const field = prefixFields[prefix];
        const value = field ? token.slice(separatorIndex + 1) : token;
        if (!value) return;

        const safe = esc(value);
        if (field) {
          parts.push(`contains(${field}, '${safe}')`);
        } else {
          parts.push(`(contains(Name, '${safe}') or contains(Note, '${safe}') or contains(User, '${safe}') or contains(Type, '${safe}'))`);
        }
      });
    }
    if (searchDate && /^\d{4}-\d{2}-\d{2}$/.test(searchDate)) {
      const selectedDate = new Date(`${searchDate}T00:00:00.000Z`);
      if (!Number.isNaN(selectedDate.getTime())) {
        const startDate = new Date(selectedDate);
        startDate.setUTCDate(startDate.getUTCDate() - 30);
        parts.push(`DateStart ge ${startDate.toISOString()}`);
        parts.push(`DateStart le ${searchDate}T23:59:59.999Z`);
      }
    } else {
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setUTCDate(startDate.getUTCDate() - 30);
      parts.push(`DateStart ge ${startDate.toISOString()}`);
      parts.push(`DateStart le ${endDate.toISOString()}`);
    }
    return parts.join(' and ');
  }, [searchAlias, searchTestType, committedKeyword, searchDate]);

  const fetchJobsPage = useCallback(async (statusFilter, skip, extra, top, orderby = 'DateStart desc') => {
    const token = await getAccessToken();
    let filter = statusFilter;
    if (extra) filter = `(${statusFilter}) and (${extra})`;
    const params = new URLSearchParams({
      '$filter': filter,
      '$orderby': orderby,
      '$top': String(top),
      '$skip': String(skip),
      '$count': 'true',
    });
    const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) throw new Error(`API error: ${resp.status}`);
    const data = await resp.json();
    return { items: data.Items || [], count: data.Count || 0 };
  }, [getAccessToken]);

  const runningQuery = useInfiniteQuery({
    queryKey: ['polaris-jobs', 'running', searchAlias, searchTestType, searchDate, committedKeyword, runningSort],
    queryFn: ({ pageParam = 0 }) => {
      const orderby = `${runningSort.column} ${runningSort.order}`;
      return fetchJobsPage(RUNNING_FILTER, pageParam, extraFilters, FETCH_SIZE_SMALL, orderby);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const queuedQuery = useInfiniteQuery({
    queryKey: ['polaris-jobs', 'queued', searchAlias, searchTestType, searchDate, committedKeyword, queuedSort],
    queryFn: ({ pageParam = 0 }) => {
      const orderby = `${queuedSort.column} ${queuedSort.order}`;
      return fetchJobsPage(QUEUED_FILTER, pageParam, extraFilters, FETCH_SIZE_SMALL, orderby);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const completedQuery = useInfiniteQuery({
    queryKey: ['polaris-jobs', 'completed', searchAlias, searchTestType, searchDate, committedKeyword, completedSort],
    queryFn: ({ pageParam = 0 }) => {
      const orderby = `${completedSort.column} ${completedSort.order}`;
      return fetchJobsPage(COMPLETED_FILTER, pageParam, extraFilters, FETCH_SIZE, orderby);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const params = new CaseInsensitiveSearchParams(location.search);
    const newUser = params.get('user') || '';
    const newTestType = params.get('testType') || '';
    const newDate = params.get('date') || '';
    const newKeyword = params.get('keyword') || '';
    setSearchAlias(newUser);
    setSearchTestType(newTestType);
    setSearchDate(newDate);
    // Only sync keyword from URL if it differs from what the user last submitted
    if (newKeyword !== keywordInputRef.current) {
      keywordDraftRef.current = newKeyword;
      setCommittedKeyword(newKeyword);
      keywordInputRef.current = newKeyword;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, accounts]);
  const isMyJobsView = Boolean(currentUserAlias) && searchAlias === currentUserAlias;
  const toggleJobsView = () => {
    const params = new CaseInsensitiveSearchParams(location.search);
    if (isMyJobsView) {
      params.delete('user');
      const nextQuery = params.toString();
      navigate(nextQuery ? `/polaris?${nextQuery}` : '/polaris');
      return;
    }
    if (currentUserAlias) {
      params.set('user', currentUserAlias);
      navigate(`/polaris?${params.toString()}`);
    }
  };

  const handleRefresh = useCallback(() => {
    navigate('/polaris');
    queryClient.invalidateQueries({ queryKey: ['polaris-jobs'] });
  }, [navigate, queryClient]);

  const handleKeywordSearch = () => {
    const keyword = keywordDraftRef.current.trim();
    setCommittedKeyword(keyword);
    keywordInputRef.current = keyword;
    keywordDraftRef.current = keyword;

    const params = new CaseInsensitiveSearchParams(location.search);
    if (keyword) params.set('keyword', keyword); else params.delete('keyword');
    navigate(`/polaris?${params.toString()}`);
  };

  const runningItems = runningQuery.data?.pages.flatMap(p => p.items) ?? [];
  const runningCount = runningQuery.data?.pages[0]?.count ?? 0;
  const queuedItems = queuedQuery.data?.pages.flatMap(p => p.items) ?? [];
  const queuedCount = queuedQuery.data?.pages[0]?.count ?? 0;
  const completedItems = completedQuery.data?.pages.flatMap(p => p.items) ?? [];
  const completedCount = completedQuery.data?.pages[0]?.count ?? 0;

  // Check if all sections are empty
  const allJobsEmpty = runningItems.length === 0 && queuedItems.length === 0 && completedItems.length === 0;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Toolbar className={classes.toolbar}>
          <ToolbarGroup>
            <ToolbarButton
              icon={<Add20Regular />}
              onClick={() => navigate('/polaris/submit')}
            >
              Submit Job
            </ToolbarButton>
            <ToolbarButton
              icon={<List20Regular />}
              onClick={() => navigate('/polaris/test-types')}
            >
              TestTypes Management
            </ToolbarButton>
            <ToolbarDivider />
            <ToolbarButton
              icon={<Eye20Regular />}
              onClick={toggleJobsView}
            >
              {isMyJobsView ? 'All Jobs' : 'My Jobs'}
            </ToolbarButton>
          </ToolbarGroup>
          <ToolbarGroup className={classes.toolbarFarGroup}>
              <SuggestionPickerModal
                type="alias"
                value={searchAlias}
                onSelect={(value) => {
                  const params = new CaseInsensitiveSearchParams(location.search);
                  if (value) params.set('user', value); else params.delete('user');
                  navigate(`/polaris?${params.toString()}`);
                }}
                buttonLabel="User"
                title="Select User"
              />
              <SuggestionPickerModal
                type="testType"
                value={searchTestType}
                onSelect={(value) => {
                  const params = new CaseInsensitiveSearchParams(location.search);
                  if (value) params.set('testType', value); else params.delete('testType');
                  navigate(`/polaris?${params.toString()}`);
                }}
                buttonLabel="Test Type"
                title="Select Test Type"
              />
            <Input
              className={classes.searchInput}
              placeholder="mm/dd/yyyy"
              type="date"
              value={searchDate}
              onChange={(e, d) => {
                const params = new CaseInsensitiveSearchParams(location.search);
                if (d.value) params.set('date', d.value); else params.delete('date');
                navigate(`/polaris?${params.toString()}`);
              }}
            />
            <div className={classes.keywordSearchWithHelp}>
              <SearchBox
                key={queryKeyword}
                className={classes.keywordSearchInput}
                placeholder="Search Keyword"
                aria-label="Search jobs by keyword or field prefix"
                contentBefore={null}
                defaultValue={queryKeyword}
                onFocus={() => setIsKeywordHelpOpen(true)}
                onBlur={() => setIsKeywordHelpOpen(false)}
                onChange={(e, d) => { keywordDraftRef.current = d.value; }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleKeywordSearch();
                  }
                }}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Search20Regular />}
                aria-label="Search jobs"
                title="Search"
                onClick={handleKeywordSearch}
              />
              {isKeywordHelpOpen && (
                <div role="tooltip" className={classes.keywordPopoverSurface}>
                  <Text weight="semibold">Filter Prefixes</Text>
                  <ul className={classes.keywordHelpList}>
                    <li><code>name:value</code> — filter Name</li>
                    <li><code>note:value</code> — filter Note</li>
                    <li><code>mt:value</code> — filter MachineType</li>
                    <li><code>err:value</code> — filter Error</li>
                  </ul>
                  <Text size={200}>
                    Separate terms with spaces. All terms use fuzzy matching and are combined with AND.
                  </Text>
                  <code className={classes.keywordExample}>test mt:V100</code>
                </div>
              )}
            </div>
            <ToolbarButton icon={<ArrowClockwise20Regular />} onClick={handleRefresh} title="Refresh" />
          </ToolbarGroup>
        </Toolbar>
      </div>

      <div className={classes.contentWrapper}>
        <JobTable
          title="Running Jobs"
          jobs={runningItems}
          totalCount={runningCount}
          loading={runningQuery.isLoading}
          loadingMore={runningQuery.isFetchingNextPage}
          error={runningQuery.error?.message}
          hasMore={runningQuery.hasNextPage}
          onLoadMore={() => runningQuery.fetchNextPage()}
          showError={false}
          showSignoff={false}
          onViewJob={handleViewJob}
          onCloneJob={handleCloneJob}
          onCancelJob={handleCancelJob}
          allJobsEmpty={allJobsEmpty}
          sortColumn={runningSort.column}
          sortOrder={runningSort.order}
          onSort={(column, order) => setRunningSort({ column, order })}
        />

        <JobTable
          title="Queued Jobs"
          jobs={queuedItems}
          totalCount={queuedCount}
          loading={queuedQuery.isLoading}
          loadingMore={queuedQuery.isFetchingNextPage}
          error={queuedQuery.error?.message}
          hasMore={queuedQuery.hasNextPage}
          onLoadMore={() => queuedQuery.fetchNextPage()}
          showError={false}
          showSignoff={false}
          onViewJob={handleViewJob}
          onCloneJob={handleCloneJob}
          onCancelJob={handleCancelJob}
          allJobsEmpty={allJobsEmpty}
          sortColumn={queuedSort.column}
          sortOrder={queuedSort.order}
          onSort={(column, order) => setQueuedSort({ column, order })}
        />

        <JobTable
          title="Completed Jobs"
          jobs={completedItems}
          totalCount={completedCount}
          loading={completedQuery.isLoading}
          loadingMore={completedQuery.isFetchingNextPage}
          error={completedQuery.error?.message}
          hasMore={completedQuery.hasNextPage}
          onLoadMore={() => completedQuery.fetchNextPage()}
          showError={true}
          showSignoff={true}
          onViewJob={handleViewJob}
          onCloneJob={handleCloneJob}
          onCancelJob={handleCancelJob}
          allJobsEmpty={allJobsEmpty}
          sortColumn={completedSort.column}
          sortOrder={completedSort.order}
          onSort={(column, order) => setCompletedSort({ column, order })}
        />
      </div>
    </div>
  );
}

export default PolarisPage;
